import { useEffect, useState } from "react";

const calculateAge = (dob) => {
  if (!dob) return "-";
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  )
    age--;
  return age;
};

const DESIGN_W = 900;
const DESIGN_H = 570;
const ATM_W_PX = 323;
const ATM_H_PX = 204;
const SCALE = ATM_W_PX / DESIGN_W;

// Shared leaf SVG background used in both cards
const LEAF_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cg opacity='0.07' fill='%23671E1A'%3E%3Cellipse cx='30' cy='30' rx='22' ry='10' transform='rotate(-40 30 30)'/%3E%3Cellipse cx='170' cy='30' rx='22' ry='10' transform='rotate(40 170 30)'/%3E%3Cellipse cx='100' cy='100' rx='28' ry='12' transform='rotate(-20 100 100)'/%3E%3Cellipse cx='30' cy='170' rx='22' ry='10' transform='rotate(40 30 170)'/%3E%3Cellipse cx='170' cy='170' rx='22' ry='10' transform='rotate(-40 170 170)'/%3E%3Cellipse cx='100' cy='50' rx='18' ry='8' transform='rotate(15 100 50)'/%3E%3Cellipse cx='50' cy='130' rx='18' ry='8' transform='rotate(-30 50 130)'/%3E%3Cellipse cx='150' cy='130' rx='18' ry='8' transform='rotate(30 150 130)'/%3E%3C/g%3E%3C/svg%3E")`;

const COLORS = ["#D00000", "#E5D500", "#EAEAEA", "#008C2E", "#001E9A"];

const CARD_BG = `linear-gradient(160deg, #E8C98A 0%, #D4A85A 50%, #C49040 100%), ${LEAF_BG}`;
const CARD_BG_BLEND = "multiply";

const ColorBar = ({ height = 14 }) => {
  return (
    <div style={{ display: "flex", height, flexShrink: 0 }}>
      {COLORS.map((c, i) => (
        <span
          key={i}
          style={{
            flex: 1,
            background: c,
            border: c === "#ffffff" ? "1px solid #ddd" : "none",
          }}
        />
      ))}
    </div>
  );
};

function FrontCardInner({ family, members }) {
  return (
    <div
      style={{
        width: DESIGN_W,
        height: DESIGN_H,
        background: `linear-gradient(160deg, #FDF5E4 0%, #F5E4C0 55%, #EDD49A 100%),
                   url("https://www.transparenttextures.com/patterns/cream-paper.png")`,
        backgroundBlendMode: "overlay",
        border: "4px solid #671E1A",
        borderRadius: 42,
        overflow: "hidden",
        fontFamily:
          "'Noto Serif Devanagari', 'Noto Sans Devanagari', 'Mangal', serif",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        boxShadow: "inset 0 0 60px rgba(103,30,26,0.06)",
      }}
    >
      {/* ── HEADER ── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "22px 30px 10px",
          gap: 12,
        }}
      >
        {/* Logo block */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,
            width: 130,
          }}
        >
          <div
            style={{
              width: 112,
              height: 112,
              borderRadius: "50%",
              border: "3px solid #B08A61",
              boxShadow: "0 2px 10px rgba(103,30,26,0.25)",
              overflow: "hidden",
              background: "#FDF5E4",
            }}
          >
            <img
              src="/colgo.png"
              alt="logo"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onError={(e) => (e.target.style.display = "none")}
            />
          </div>
          <div
            style={{
              color: "#D00000",
              fontWeight: 700,
              fontSize: 19,
              marginTop: 6,
              letterSpacing: 0.5,
              textAlign: "center",
            }}
          >
            जैन समग्र
          </div>
        </div>

        {/* Title */}
        <div
          style={{
            flex: 1,
            textAlign: "center",
            paddingTop: 32,
          }}
        >
          <div
            style={{
              fontSize: 54,
              fontWeight: 700,
              color: "#671E1A",
              fontFamily: "'Noto Serif Devanagari', serif",
              textShadow:
                "1px 1px 0 #FDF5E4, 2px 2px 0 #8E493A, 3px 3px 4px rgba(103,30,26,0.18)",
              lineHeight: 1.18,
              letterSpacing: 0.5,
            }}
          >
            जैन समग्र परिवार कार्ड
          </div>
        </div>

        {/* Family ID box */}
        <div
          style={{
            width: 160,
            flexShrink: 0,
            borderRadius: 10,
            overflow: "hidden",
            border: "2px solid #671E1A",
            boxShadow: "0 3px 14px rgba(103,30,26,0.18)",
          }}
        >
          <div
            style={{
              background: "#F9EDD4",
              color: "#671E1A",
              textAlign: "center",
              padding: "5px 8px",
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            परिवार आई. डी.
          </div>
          <div
            style={{
              background: "#ffffff",
              textAlign: "center",
              fontSize: 15,
              fontWeight: 800,
              color: "#2B2823",
              padding: "5px 8px",
              letterSpacing: 3,
              minHeight: 36,
            }}
          >
            {family.id || ""}
          </div>
          <ColorBar height={6} />
        </div>
      </div>

      {/* ── INFO PANEL ── */}
      <div
        style={{
          margin: "0 24px 28px",
          background: "rgba(253,245,228,0.78)",
          borderRadius: 42,
          padding: "18px 36px 16px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxShadow: "inset 0 1px 8px rgba(103,30,26,0.07)",
          border: "1px solid rgba(176,138,97,0.3)",
        }}
      >
        {/* Full-width rows */}
        <InfoRow
          label="परिवार का मुखिया"
          value={family.basic?.headName || ""}
        />
        <InfoRow
          label="संबंधित मंदिर का नाम"
          value={family.basic?.Mandirtype || ""}
        />
        <InfoRow
          label="स्थाई निवास"
          value={
            [
              family.basic?.gram,
              family.basic?.urbanArea,
              family.basic?.panchayat,
              family.basic?.block,
            ]
              .filter(Boolean)
              .join(", ") || ""
          }
        />

        {/* 3-col row: राज्य / जिला / ब्लॉक */}
        <div style={{ display: "flex", gap: 20 }}>
          <InfoRow label="राज्य" value={family.basic?.state || ""} flex />
          <InfoRow label="जिला" value={family.basic?.city || ""} flex />
          <InfoRow label="ब्लॉक" value={family.basic?.block || ""} flex />
        </div>

        {/* Bottom row: कुल सदस्य + संपर्क */}
        <div style={{ display: "flex", gap: 20 }}>
          <InfoRow
            label="परिवार में कुल सदस्य"
            value={family.basic?.Totalmember || members.length || ""}
            flex
          />
          <InfoRow label="संपर्क" value={family.basic?.mobile || ""} flex />
        </div>
      </div>

      {/* ── BOTTOM COLOR STRIP ── */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
        <ColorBar height={16} />
      </div>
    </div>
  );
}

function InfoRow({ label, value, flex }) {
  return (
    <div
      style={{
        flex: flex ? 1 : undefined,
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: "#671E1A",
          fontFamily: "'Noto Serif Devanagari', serif",
          whiteSpace: "nowrap",
          flexShrink: 0,
          letterSpacing: 0.2,
        }}
      >
        {label} :
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 21,
          fontWeight: 800,
          color: "#2B2823",
          paddingBottom: 3,
          paddingLeft: 8,
          backgroundImage:
            "radial-gradient(circle, #D06B5F 1.2px, transparent 1.2px)",
          backgroundSize: "8px 100%",
          backgroundPosition: "0 bottom",
          backgroundRepeat: "repeat-x",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function BackCardInner({ members, chunkIndex, printDate }) {
  const chunk = members.slice(chunkIndex * 6, chunkIndex * 6 + 6);
  const startIdx = chunkIndex * 6;

  return (
    <div
      style={{
        width: DESIGN_W,
        height: DESIGN_H,
        background: `linear-gradient(160deg, #FDF5E4 0%, #F5E4C0 55%, #EDD49A 100%),
                   url("https://www.transparenttextures.com/patterns/cream-paper.png")`,
        backgroundBlendMode: "overlay",
        border: "4px solid #671E1A",
        borderRadius: 40,
        overflow: "hidden",
        fontFamily: "'Noto Serif Devanagari', 'Noto Sans Devanagari', serif",
        display: "flex",
        flexDirection: "column",
        padding: "22px 24px 0",
        position: "relative",
      }}
    >
      {/* ── HEADER ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 14,
        }}
      >
        {/* Logo */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,
            width: 100,
          }}
        >
          <div
            style={{
              width: 82,
              height: 82,
              borderRadius: "50%",
              border: "3px solid #B08A61",
              boxShadow: "0 2px 10px rgba(103,30,26,0.25)",
              overflow: "hidden",
              background: "#FDF5E4",
            }}
          >
            <img
              src="/colgo.png"
              alt="logo"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onError={(e) => (e.target.style.display = "none")}
            />
          </div>
          <div
            style={{
              color: "#D00000",
              fontWeight: 700,
              fontSize: 16,
              marginTop: 4,
              textAlign: "center",
            }}
          >
            जैन समग्र
          </div>
        </div>

        {/* Leaf-shaped banner */}
        <div
          style={{
            flex: 1,
            position: "relative",
            height: 72,
            display: "flex",
            alignItems: "center",
          }}
        >
          <svg
            viewBox="0 0 700 72"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
            }}
          >
            {/* Leaf shape: pointed on both left and right */}
            <path
              d="M36,36 Q60,2 120,4 L580,4 Q640,2 664,36 Q640,70 580,68 L120,68 Q60,70 36,36 Z"
              fill="url(#bannerGrad)"
            />
            {/* Inner highlight line top */}
            <path
              d="M50,36 Q72,8 128,10 L572,10 Q628,8 650,36"
              fill="none"
              stroke="#B08A61"
              strokeWidth="1.5"
              opacity="0.6"
            />
            {/* Inner highlight line bottom */}
            <path
              d="M50,36 Q72,64 128,62 L572,62 Q628,64 650,36"
              fill="none"
              stroke="#B08A61"
              strokeWidth="1.5"
              opacity="0.6"
            />
            <defs>
              <linearGradient id="bannerGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8E493A" />
                <stop offset="40%" stopColor="#671E1A" />
                <stop offset="100%" stopColor="#4a1210" />
              </linearGradient>
            </defs>
          </svg>
          {/* Text on top of SVG */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              textAlign: "center",
              fontSize: 32,
              fontWeight: 800,
              color: "#FDF5E4",
              fontFamily: "'Noto Serif Devanagari', serif",
              textShadow: "0 1px 4px rgba(0,0,0,0.5)",
              letterSpacing: 0.5,
              lineHeight: 1,
            }}
          >
            परिवार के सदस्यों का विवरण
          </div>
        </div>
      </div>

      {/* ── TABLE ── */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
            border: "2px solid #671E1A",
            borderRadius: 8,
            overflow: "hidden",
            fontSize: 19,
            fontFamily: "'Noto Serif Devanagari', serif",
          }}
        >
          <colgroup>
            <col style={{ width: "6%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "22%" }} />
          </colgroup>
          <thead>
            <tr>
              {[
                "क्र.",
                "सदस्य आईडी",
                "सदस्य का नाम",
                "आयु",
                "लिंग",
                "संबंध",
              ].map((h, i) => (
                <th
                  key={i}
                  style={{
                    background:
                      "linear-gradient(180deg, #8E493A 0%, #671E1A 100%)",
                    color: "#FDF5E4",
                    border: "1px solid #8E493A",
                    padding: "9px 6px",
                    fontWeight: 800,
                    textAlign: "center",
                    fontSize: 20,
                    letterSpacing: 0.3,
                    textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => {
              const m = chunk[i];
              const bg = i % 2 === 0 ? "#FDFAF4" : "#F5EDD8";
              return (
                <tr key={i} style={{ background: bg }}>
                  <td style={tdS}>{m ? startIdx + i + 1 : ""}</td>
                  <td style={tdS}>{m ? m.memberId || "-" : ""}</td>
                  <td style={{ ...tdS, textAlign: "left", paddingLeft: 12 }}>
                    {m ? m.name || "-" : ""}
                  </td>
                  <td style={tdS}>
                    {m ? (m.dob ? calculateAge(m.dob) : "-") : ""}
                  </td>
                  <td style={tdS}>
                    {m
                      ? m.gender === "पुरुष"
                        ? "पु."
                        : m.gender === "महिला"
                          ? "म."
                          : "-"
                      : ""}
                  </td>
                  <td style={tdS}>{m ? m.relation || "-" : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── FOOTER ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 4px 14px",
          fontSize: 18,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#671E1A" strokeWidth="2" />
            <ellipse
              cx="12"
              cy="12"
              rx="4"
              ry="10"
              stroke="#671E1A"
              strokeWidth="1.5"
            />
            <line
              x1="2"
              y1="12"
              x2="22"
              y2="12"
              stroke="#671E1A"
              strokeWidth="1.5"
            />
          </svg>
          <span style={{ fontWeight: 700, color: "#671E1A", fontSize: 19 }}>
            jainsamagra.com
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#671E1A" strokeWidth="2" />
            <line
              x1="12"
              y1="7"
              x2="12"
              y2="13"
              stroke="#671E1A"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="12" cy="16.5" r="1.3" fill="#671E1A" />
          </svg>
          <span style={{ fontWeight: 600, color: "#2B2823", fontSize: 18 }}>
            पंजीयन दिनांक : {printDate}
          </span>
        </div>
      </div>

      {/* ── BOTTOM COLOR STRIP ── */}
      <div style={{ margin: "0 -24px" }}>
        <ColorBar height={14} />
      </div>
    </div>
  );
}

const tdS = {
  border: "1px solid #C8A96E",
  padding: "8px 6px",
  textAlign: "center",
  verticalAlign: "middle",
  color: "#2B2823",
  fontSize: 19,
  fontFamily: "'Noto Serif Devanagari', serif",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

function FieldRow({ label, value, flex }) {
  return (
    <div
      style={{
        flex: flex ? 1 : undefined,
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: 8,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: "#671E1A",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {label} :
      </span>
      <span
        style={{
          fontSize: 17,
          color: "#2B2823",
          borderBottom: "2px dashed #D06B5F",
          paddingBottom: 4,
          flex: 1,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ATMCard({ children }) {
  return (
    <div
      style={{
        width: ATM_W_PX,
        height: ATM_H_PX,
        overflow: "hidden",
        borderRadius: 14,
        boxShadow: "0 6px 24px rgba(0,0,0,0.22)",
        flexShrink: 0,
        position: "relative",
      }}
    >
      <div
        style={{
          transform: `scale(${SCALE})`,
          transformOrigin: "top left",
          width: DESIGN_W,
          height: DESIGN_H,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function ParivarCardLayout() {
  const [family, setFamily] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("parivarCardData");
      if (raw) setFamily(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    // auto-print if opened with ?autoprint=1
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("autoprint")) {
        // allow a short delay for layout/render then trigger print
        setTimeout(() => {
          window.print();
        }, 600);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  if (!family) {
    return (
      <div style={{ textAlign: "center", padding: 60, fontFamily: "serif" }}>
        <h3>डेटा लोड हो रहा है...</h3>
        <p style={{ color: "#888", marginTop: 10 }}>
          कृपया परिवार कार्ड पेज से "सिर्फ कार्ड प्रिंट" बटन दबाएं।
        </p>
      </div>
    );
  }

  const members = family.members ? Object.values(family.members) : [];
  const printDate = new Date().toLocaleDateString("hi-IN");
  const totalChunks = Math.max(1, Math.ceil(members.length / 6));

  const sheets = [];
  // Each sheet: front on LEFT, back on RIGHT — fold along center to get single card
  for (let i = 0; i < totalChunks; i++) {
    sheets.push({ front: i, back: i });
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700;800&family=Noto+Serif+Devanagari:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { background: #888; font-family: 'Noto Sans Devanagari', serif; }
        .no-print { display: flex; }
        @media print {
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { background: white; }
          .no-print { display: none !important; }
          @page { size: A4 portrait; margin: 10mm; }
          .sheet { page-break-after: always; margin: 0 !important; box-shadow: none !important; }
          .sheet, .sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Toolbar */}
      <div
        className="no-print"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 999,
          background: "linear-gradient(90deg, #7a0d0d, #c40000)",
          padding: "10px 24px",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ color: "#ffe066", fontWeight: 800, fontSize: 18 }}>
          🎫 जैन समग्र परिवार कार्ड — {family.basic?.headName || ""}{" "}
          &nbsp;|&nbsp; ID: {family.id || ""}
        </div>
        <button
          onClick={() => window.print()}
          style={{
            background: "#fff",
            color: "#7a0d0d",
            border: "none",
            borderRadius: 8,
            padding: "8px 28px",
            fontSize: 15,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          🖨️ प्रिंट करें
        </button>
      </div>

      {/* Visible print button (also hides in print via .no-print) */}
      <div style={{ textAlign: "center", marginTop: 58 }} className="no-print">
        <button
          onClick={() => window.print()}
          style={{
            background: "#fff",
            color: "#7a0d0d",
            border: "none",
            borderRadius: 8,
            padding: "10px 36px",
            fontSize: 16,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          }}
        >
          🖨️ प्रिंट प्रीव्यू / प्रिंट करें
        </button>
      </div>

      <div style={{ paddingTop: 60 }}>
        {sheets.map((sheet, si) => (
          <div
            key={si}
            className="sheet"
            style={{
              width: "210mm",
              minHeight: "148mm",
              background: "white",
              margin: "20px auto",
              padding: "10mm 8mm",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              position: "relative",
            }}
          >
            {/* Fold line in center */}
            <div className="no-print" style={{
              position: "absolute",
              left: "50%",
              top: 0,
              bottom: 0,
              width: 1,
              borderLeft: "2px dashed #aaa",
              transform: "translateX(-50%)",
              zIndex: 10,
            }} />

            {/* FRONT — left side */}
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", paddingRight: 1 }}>
              <ATMCard>
                <FrontCardInner family={family} members={members} />
              </ATMCard>
            </div>

            {/* BACK — right side */}
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-start", paddingLeft: 0 }}>
              <ATMCard>
                <BackCardInner members={members} chunkIndex={sheet.back} printDate={printDate} />
              </ATMCard>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
